import React, { useEffect } from "react";
import { Steps, Button, Card, Alert, Tag } from "antd";
import {
  CloseOutlined,
  RobotOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useBookingWizard } from "../../contexts/BookingWizardContext";
import {
  BookingWizardV2Provider,
  useBookingWizardV2,
  V2_TOTAL_STEPS,
} from "./context/BookingWizardV2Context";
import { InvoiceUploadStep } from "./steps/InvoiceUploadStep";
import { SmartDetailsStep } from "./steps/SmartDetailsStep";
import { DeliveryDetails } from "../BookingWizard/steps/DeliveryDetails";
import { ReviewDetails } from "../BookingWizard/steps/ReviewDetails";
import { ProductFormProvider } from "../../contexts/ProductFormContext";
import { PricingCalculatorEnhanced } from "../BookingWizard/PricingCalculatorEnhanced";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";
import { getEnv } from "../../lib/env";
import { CommercialInvoiceSidePanel } from "./components/CommercialInvoiceSidePanel";

interface BookingWizardV2Props {
  onClose: () => void;
}

const V2StepContent = ({
  setV2Step,
  v2Step,
}: {
  v2Step: number;
  setV2Step: (step: number) => void;
}) => {
  const { setCurrentStep, state } = useBookingWizard();

  useEffect(() => {
    if (v2Step >= 2) {
      const target = v2Step === 2 ? 4 : 5;
      if (state.currentStep !== target) {
        setCurrentStep(target);
      }
    }
  }, [v2Step, setCurrentStep, state.currentStep]);

  switch (v2Step) {
    case 0:
      return <InvoiceUploadStep />;
    case 1:
      return <SmartDetailsStep />;
    case 2:
      return (
        <DeliveryDetails
          nextStepOverride={4}
          onNextOverride={() => setV2Step(3)}
          onPreviousOverride={() => setV2Step(1)}
          onGoToProductsOverride={() => setV2Step(1)}
          useV2Navigation
        />
      );
    case 3:
      return (
        <ReviewDetails
          prevStepOverride={4}
          onPreviousOverride={() => setV2Step(2)}
          useV2Navigation
        />
      );
    default:
      return null;
  }
};

const BookingWizardV2Inner = ({ onClose }: BookingWizardV2Props) => {
  const { t } = useTranslation();
  const { state } = useBookingWizard();
  const { v2Step, setV2Step, analysisResult } = useBookingWizardV2();
  const { isPricingCalculatorEnabled } = useFeatureFlags();
  const useTemplatePricingSystem =
    getEnv("VITE_USE_TEMPLATE_PRICING", "true") === "true";

  const steps = [
    { title: t("bookingWizardV2.steps.invoice") },
    { title: t("bookingWizardV2.steps.details") },
    { title: t("bookingWizard.steps.deliveryDetails") },
    { title: t("bookingWizard.steps.review") },
  ];

  const shouldShowPricing = isPricingCalculatorEnabled && v2Step >= 1;
  const useBannerPricing = shouldShowPricing && v2Step === 1;

  const handleStepClick = (step: number) => {
    if (step < v2Step) {
      setV2Step(step);
    }
  };

  const stepContent = (
    <V2StepContent setV2Step={setV2Step} v2Step={v2Step} />
  );

  const showInvoicePanel =
    v2Step === 1 && Boolean(state.commercial_invoice_file?.trim());

  const invoicePanel =
    showInvoicePanel && state.commercial_invoice_file ? (
      <CommercialInvoiceSidePanel
        commercialInvoiceFile={state.commercial_invoice_file}
        bookingId={state.id}
        extractedText={analysisResult?.extractedTextPreview}
      />
    ) : null;

  const pricingCalculator = shouldShowPricing ? (
    <PricingCalculatorEnhanced
      useTemplatePricingSystem={useTemplatePricingSystem}
      layout={useBannerPricing ? "banner" : "sidebar"}
    />
  ) : null;

  const stepBody = invoicePanel ? (
    <div
      style={{
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
        flexWrap: "wrap",
        overflow: "visible",
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 280,
          display: "flex",
          flexDirection: "column",
          gap: useBannerPricing ? 24 : 0,
        }}
      >
        {stepContent}
        {useBannerPricing ? pricingCalculator : null}
      </div>
      {invoicePanel}
    </div>
  ) : (
    stepContent
  );

  const mainContent = (
    <>
      {invoicePanel ? (
        stepBody
      ) : shouldShowPricing ? (
        <div
          style={{
            display: "flex",
            flexDirection: useBannerPricing ? "column" : "row",
            gap: useBannerPricing ? 24 : 8,
          }}
        >
          <div style={{ flex: 1, minWidth: 0, width: "100%" }}>{stepContent}</div>
          {pricingCalculator}
        </div>
      ) : (
        stepContent
      )}
    </>
  );

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <RobotOutlined style={{ color: "#6366f1" }} />
            {t("bookingWizardV2.title")}
            <Tag color="purple" icon={<ThunderboltOutlined />}>
              AI
            </Tag>
          </span>
          <Button onClick={onClose} icon={<CloseOutlined />} />
        </div>
      }
      style={{ width: "100%", overflow: "visible" }}
      styles={{ body: { padding: 24, overflow: "visible" } }}
    >
      {state.status === "rejected" && state.rejection_reason && (
        <Alert
          message={t("bookingWizard.rejectionReason")}
          description={state.rejection_reason}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Steps
        current={v2Step}
        onChange={handleStepClick}
        items={steps}
        style={{ marginBottom: 8 }}
      />

      {shouldShowPricing ? (
        <ProductFormProvider>
          <div style={{ marginTop: 24, overflow: "visible" }}>{mainContent}</div>
        </ProductFormProvider>
      ) : (
        <div style={{ marginTop: 24, overflow: "visible" }}>{mainContent}</div>
      )}
    </Card>
  );
};

export const BookingWizardV2 = ({
  onClose,
  initialStep = 0,
}: BookingWizardV2Props & { initialStep?: number }) => (
  <BookingWizardV2Provider initialStep={initialStep}>
    <BookingWizardV2Inner onClose={onClose} />
  </BookingWizardV2Provider>
);

export { V2_TOTAL_STEPS };
